import "./list-registration.scss";

import { GetRegistrationDto, MessageResponseDto } from "@api/api";
import CalendarScheduler from "@domain/calendar/calendar-scheduler";
import { ApproveRegistrationFormSchema } from "@factory/form/form.rule";
import { GetRegistrationModel } from "@factory/model/query-model-registration";
import { useQyGetRegistration } from "@factory/query/registration.query";
import { useQyGetNextAvailableDateRestricted } from "@factory/query/schedule-exception.query";
import { useFormApiMapper } from "@factory/services/form-api-mapper.hook";
import { TableSort } from "@shared/models/table-sort";
import ErrorSection from "@shared/ui/error-section/error-section";
import HeaderContent from "@shared/ui/header-content/header-content";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import SearchInput from "@shared/ui/search-input/search-input";
import SkeletonList from "@shared/ui/skeleton-list/skeleton-list";
import { useDateTimeContext } from "@shared/utility/date-time-service/date-time.context";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { DataTable, DataTableStateEvent } from "primereact/datatable";
import { Paginator, PaginatorPageChangeEvent } from "primereact/paginator";
import { Sidebar } from "primereact/sidebar";
import { TabPanel, TabView } from "primereact/tabview";
import { useState } from "react";
import { CiSearch } from "react-icons/ci";
import { MdClear } from "react-icons/md";
import { useNavigate, useParams } from "react-router-dom";

import { FilterRegistration } from "../filter-registration/filter-registration";
import { useFilterRegistrationContext } from "../filter-registration/filter-registration.context";
import { useApproveRegistration } from "../new-registration/approve-registration.hook";

export function ListRegistration() {
  // THIS IS THE LOCAL DECLARATION
  const navigate = useNavigate();
  const { client } = useParams<{ client: string }>();
  const [rowLimit, setRowLimit] = useState(999);
  const [pageNumber, setPageNumber] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [first, setFirst] = useState(0);
  const [tableSort, setTableSort] = useState<TableSort>({
    sortField: undefined,
    sortOrder: undefined,
  });
  const [sort, setSort] = useState({});
  const [filterPanel, setFilterPanel] = useState(false);
  const { filterChipsElement, statusFilterElement, getFilterEntityValues } =
    useFilterRegistrationContext();
  const { formatDateLocal } = useDateTimeContext();
  const { approveRegistration, isCreating } = useApproveRegistration();
  const { showError, showSuccess } = useNotificationContext();

  const { mapApproveRegistration } = useFormApiMapper();
  // THIS IS THE QUERY DECLARATION
  const { data: nextAvailableData } = useQyGetNextAvailableDateRestricted(
    client || "",
  );

  const {
    data: registrationResponse,
    isLoading,
    isError,
    error,
  } = useQyGetRegistration(
    searchTerm,
    rowLimit,
    pageNumber,
    sort,
    getFilterEntityValues(),
  );

  // THIS IS THE LOCAL DECLARATION
  const handleSearch = (searchTerm: string) => {
    setSearchTerm(searchTerm);
  };
  const handleTableSort = (e: DataTableStateEvent) => {
    setTableSort({
      sortField: e.sortField,
      sortOrder: e.sortOrder,
    });
    const order = e.sortOrder === 1 ? "ASC" : "DESC";
    setSort({ [e.sortField]: order });
  };
  const navigateToRecord = (item: GetRegistrationModel) => {
    navigate(`${item.code}`);
  };
  const onPageChange = (event: PaginatorPageChangeEvent) => {
    const offsetValue = event.page * rowLimit;
    setFirst(event.first);
    setPageNumber(offsetValue);
    setRowLimit(event.rows);
  };
  const handleNew = () => {
    navigate(`new`);
  };

  const colName = (cell: GetRegistrationDto) => {
    return (
      <div>
        {cell.first_name.toUpperCase()} {cell?.middle_name?.toUpperCase()}{" "}
        {cell.last_name.toUpperCase()}
      </div>
    );
  };

  const handleApproveDecline = (
    code: string,
    status: "APPROVE" | "DECLINE",
  ) => {
    const message =
      status === "APPROVE"
        ? "Are you sure you want to approve this registration?"
        : "Are you sure you want to decline this registration?";

    confirmDialog({
      message,
      header: `${status === "APPROVE" ? "Approve" : "Decline"} Confirmation`,
      icon: "pi pi-info-circle",
      defaultFocus: "reject",
      acceptClassName:
        status === "APPROVE" ? "p-button-success" : "p-button-danger",
      accept: () => {
        const formData: ApproveRegistrationFormSchema = {
          code: code,
          status: status,
        };

        const approveRegistrationData = mapApproveRegistration(formData);

        approveRegistration(approveRegistrationData, {
          onSuccess: (response: MessageResponseDto) => {
            showSuccess(`Registration ${status.toLowerCase()}d successfully`);
          },
          onError: (error) => {
            showError(
              `Failed to ${status.toLowerCase()} registration: ${error.message}`,
            );
          },
        });
      },
    });
  };

  const statusColumn = (rowData: GetRegistrationDto) => {
    const status = rowData.status || "PENDING";
    const statusColors: Record<string, string> = {
      PENDING: "bg-blue-100 text-blue-800",
      APPROVE: "bg-green-100 text-green-800",
      DECLINE: "bg-red-100 text-red-800",
    };

    return (
      <span
        className={`px-2 py-1 rounded-full text-xs font-medium ${
          statusColors[status] || "bg-gray-100 text-gray-800"
        }`}
      >
        {status}
      </span>
    );
  };

  // Action buttons column
  const actionColumn = (rowData: GetRegistrationDto) => {
    const status = rowData.status || "PENDING";

    if (status === "PENDING") {
      return (
        <div className="flex gap-1">
          <Button
            icon="pi pi-check"
            tooltip="Approve"
            disabled={isCreating}
            severity="success"
            size="small"
            outlined
            onClick={() => handleApproveDecline(rowData.code!, "APPROVE")}
            className="p-1"
          />
          <Button
            icon="pi pi-times"
            tooltip="Decline"
            disabled={isCreating}
            severity="danger"
            size="small"
            outlined
            onClick={() => handleApproveDecline(rowData.code!, "DECLINE")}
            className="p-1"
          />
        </div>
      );
    } else {
      return (
        <div className="flex gap-1">
          <Button
            icon="pi pi-eye"
            tooltip="View Details"
            severity="info"
            size="small"
            outlined
            onClick={() => navigateToRecord(rowData)}
            className="p-1"
          />
        </div>
      );
    }
  };
  // THIS IS THE LOCAL RENDERS
  const displayLoading = (
    <div className="card">
      <SkeletonList count={4} />
    </div>
  );
  const displayError = (
    <div className="card">
      <ErrorSection title="Error Occured" message={(error as any)?.message} />
    </div>
  );
  const filterElement = (
    <section>
      <div className="flex gap-4 items-center mb-2">
        <SearchInput
          clearIcon={<MdClear />}
          searchIcon={<CiSearch />}
          searchTerm={searchTerm}
          onSearch={handleSearch}
          placeholder="Search items"
          className="w-full block"
        />
        <Button
          label="Filter"
          severity="secondary"
          badgeClassName="p-badge-danger"
          onClick={() => setFilterPanel(true)}
          outlined
        />
      </div>

      {filterChipsElement}
      <Sidebar visible={filterPanel} onHide={() => setFilterPanel(false)}>
        <FilterRegistration />
      </Sidebar>
    </section>
  );
  const grid = (
    <section className="mt-6">
      <h5 className="mb-2">
        Total: <b>{registrationResponse?.count}</b>
      </h5>

      <DataTable
        value={registrationResponse?.data}
        selectionMode="single"
        onSelectionChange={(e) => navigateToRecord(e.value)}
        onSort={handleTableSort}
        sortField={tableSort.sortField}
        sortOrder={tableSort.sortOrder}
      >
        <Column body={(cell) => colName(cell)} header="Name" sortable></Column>
        <Column field="gender" header="Gender" sortable></Column>
        <Column field="address" header="Address" sortable></Column>
        <Column field="mobile_number" header="Mobile_number" sortable></Column>
        <Column field="email" header="Email" sortable></Column>
        <Column
          header="Date"
          sortable
          body={(rowData) =>
            rowData.date ? formatDateLocal(rowData.date) : "N/A"
          }
        />
        <Column field="time" header="Time" sortable></Column>
        <Column body={statusColumn} header="Status" sortable field="status" />
        <Column
          body={actionColumn}
          header="Actions"
          style={{ width: "120px" }}
        />
      </DataTable>

      <Paginator
        first={first}
        rows={rowLimit}
        totalRecords={registrationResponse?.count}
        rowsPerPageOptions={[10, 20, 30]}
        onPageChange={onPageChange}
      />
    </section>
  );

  return (
    <div id="ListRegistration" className="registration">
      <HeaderContent title={<h1>Appointments</h1>}>
        {/* <span className="flex gap-2">
          <Button label="New" onClick={handleNew} />
        </span> */}
      </HeaderContent>

      <div className="px-7 pt-4 pb-2 border-b border-gray-200">
        {statusFilterElement}
      </div>

      <div className="p-7">
        <TabView>
          <TabPanel header="Scheduler">
            {isLoading && displayLoading}
            {isError && !isLoading && displayError}
            {!isLoading && !isError && (
              <CalendarScheduler
                registrationData={registrationResponse?.data || []}
                defaultDate={
                  nextAvailableData?.next_available_date ||
                  nextAvailableData?.default_date
                }
              />
            )}
          </TabPanel>
          <TabPanel header="Appointment List">
            {filterElement}
            {isLoading && displayLoading}
            {isError && !isLoading && displayError}
            {!isLoading && !isError && grid}
          </TabPanel>
        </TabView>
      </div>
      <ConfirmDialog />
    </div>
  );
}

export default ListRegistration;
